import {computed, ref} from 'vue';
import {ADAC_CATEGORIES, AdacCategory, adacAlert, adacHasFindings} from '@/amelipro/adacCategories';
import {isTemporaryNir, portalNir} from '@/amelipro/nir';
import {AdacVerdict, AmeliproSignInRefusal} from '@/amelipro/interfaces';
import useAmelipro from '@/amelipro/composables/useAmelipro';
import useCpsLocator from '@/amelipro/composables/useCpsLocator';
import {useCpsStore} from '@/stores/cps.store';

/** Why a category could not be read, in the terms the screen speaks. */
export type AdacRefusal =
    | AmeliproSignInRefusal
    /** The pharmacy has a gateway, but it never answered. */
    | 'unreachable'
    /** Neither the detection nor the patient's file gave a NIR: there is no patient to ask about. */
    | 'no-nir'
    /** The detection gave something that is not a NIR: a partial value, a consumer key. */
    | 'invalid-nir'
    /** An emulated detection without the insured's NIR: ADAC has nobody to ask about. */
    | 'emulated'
    /** A temporary NIR the portal would not select a patient with. */
    | 'temporary-nir'
    /** The portal refused for a reason of its own. */
    | 'failed';

/**
 * The answer as a whole, as the ADAC card and the collapsed entry say it.
 *
 * Connect weighs no quantity: only ADAC's own flag reads as « to check », and
 * only a complete answer with nothing reimbursed reads as clear. Anything else
 * — reimbursements, or a category that did not answer — is said as it is.
 */
export interface AdacGist {
    /** ADAC flagged a category; something was reimbursed; the answer is incomplete; nothing was reimbursed. */
    tone: 'alert' | 'reimbursed' | 'partial' | 'empty';
    /** The categories ADAC flagged. */
    flagged: AdacCategoryCheck[];
    /** The categories with reimbursements over the period. */
    reimbursed: AdacCategoryCheck[];
    /** The categories that did not answer. */
    unknown: AdacCategoryCheck[];
    /** The categories that answered. */
    answered: AdacCategoryCheck[];
    /** The gist in a few words. */
    message: string;
}

/** Where one category's check stands. */
export interface AdacCategoryCheck {
    category: AdacCategory;
    state: 'running' | 'done' | 'failed';
    /** What ADAC answered, once it has. */
    verdict: AdacVerdict | null;
    /** Why it did not, when it did not. */
    refusal: AdacRefusal | null;
}

/** The refusals the shell spells out at the end of its error strings. */
const REFUSALS: readonly AmeliproSignInRefusal[] = ['no-code', 'no-certificate', 'wrong-code', 'locked', 'final-try', 'no-token'];

/**
 * Read the shell's error string as something to say to a pharmacist.
 *
 * The shell reports `amelipro:not-authenticated:wrong-code` and the like — a
 * cause worth naming, because the pharmacist can act on it. Everything else
 * (`amelipro:adac-read-failed:401`, a session that lapsed mid-flight) is a
 * failure with nothing to do about it but try again.
 *
 * @param error What the shell, or the gateway, reported.
 */
const refusalOf = (error: string, nir = ''): AdacRefusal => {
    if (error.includes(':invalid-nir')) {
        return 'invalid-nir';
    }

    // A temporary number is sent as it is, and the portal is the one to know
    // it: when it refuses the patient (a 4xx), that is the reason. A 5xx is
    // the portal failing, whatever the NIR, and stays a failure to retry.
    const selection = /:select-patient-failed:(\d{3})/.exec(error);

    if (selection && Number(selection[1]) >= 400 && Number(selection[1]) < 500 && isTemporaryNir(nir)) {
        return 'temporary-nir';
    }

    return REFUSALS.find(candidate => error.endsWith(`:${candidate}`)) ?? 'failed';
};

/**
 * Say why nothing came back, plainly and with what to do about it.
 *
 * Written down once: the same reason is read on the notification's own tab,
 * which is all a pharmacist sees while the panel is collapsed, and in the
 * panel itself. Two wordings for one refusal would eventually disagree.
 *
 * @param refusal What stopped the check.
 */
export const adacRefusalMessage = (refusal: AdacRefusal | null): string => {
    switch (refusal) {
        case 'no-nir':
            return 'NIR du patient inconnu : saisissez-le pour interroger ADAC';
        case 'invalid-nir':
            return 'NIR du patient incomplet : saisissez-le pour interroger ADAC';
        case 'emulated':
            return 'Détection émulée : saisissez le NIR du patient pour interroger ADAC';
        case 'temporary-nir':
            return 'NIR provisoire : AméliePro ne reconnaît pas encore ce patient';
        case 'unreachable':
            return 'Poste passerelle injoignable';
        case 'no-code':
            return 'Configuration ADAC à terminer';
        case 'wrong-code':
            return 'Code porteur refusé par la carte';
        case 'locked':
            return 'Carte CPS bloquée';
        case 'final-try':
            return 'Carte CPS : dernier essai, code non présenté';
        case 'no-token':
            return 'Aucune carte CPS dans le lecteur';
        case 'no-certificate':
            return 'Carte CPS inconnue de Windows : retirez-la puis réinsérez-la';
        default:
            return 'AméliePro n\'a pas répondu';
    }
};

/**
 * Read a finished check as a whole.
 *
 * @param checks One entry per category asked for.
 */
export const adacGist = (checks: AdacCategoryCheck[]): AdacGist => {
    const answered = checks.filter(check => check.state === 'done' && check.verdict !== null);
    const flagged = answered.filter(check => adacAlert(check.verdict!));
    const reimbursed = answered.filter(check => adacHasFindings(check.verdict!));
    const unknown = checks.filter(check => check.state === 'failed' || (check.state === 'done' && check.verdict === null));
    const subjects = (count: number): string => `${count} ${count > 1 ? 'sujets' : 'sujet'}`;

    if (flagged.length) {
        return {tone: 'alert', flagged, reimbursed, unknown, answered, message: `${subjects(flagged.length)} à vérifier`};
    }

    if (reimbursed.length) {
        return {tone: 'reimbursed', flagged, reimbursed, unknown, answered, message: `Remboursements sur ${subjects(reimbursed.length)}`};
    }

    if (unknown.length) {
        return {
            tone: 'partial',
            flagged,
            reimbursed,
            unknown,
            answered,
            message: `Aucun remboursement sur ${answered.length}/${checks.length} sujets`,
        };
    }

    return {tone: 'empty', flagged, reimbursed, unknown, answered, message: 'Aucun remboursement'};
};

/** The colour of a gist: ADAC's flag in orange, a clear answer in green, the rest neutral. */
export const ADAC_GIST_COLOURS: Record<AdacGist['tone'], string> = {
    alert: 'bg-orange',
    reimbursed: 'bg-primary',
    partial: 'bg-gray',
    empty: 'bg-green',
};

/**
 * Whether a refusal waits on a NIR the pharmacist can type: none was found,
 * the one found is not one, the detection was emulated without it. The
 * pharmacist has it on the LGO screen in front of them.
 *
 * @param refusal What stopped the check.
 */
export const adacNeedsNir = (refusal: AdacRefusal | null): boolean =>
    refusal === 'no-nir' || refusal === 'invalid-nir' || refusal === 'emulated';

/**
 * Whether another go is worth offering: a lapsed session, a gateway that was
 * busy. A missing NIR will not appear by the button being pressed again, and
 * a temporary NIR the portal does not know will not be known a second later.
 *
 * @param refusal What stopped the check.
 */
export const adacRetryable = (refusal: AdacRefusal | null): boolean =>
    !adacNeedsNir(refusal) && refusal !== 'temporary-nir';

/**
 * Ask ADAC about the patient at the counter, category by category.
 *
 * The post holding the CPS card asks the portal itself; every other post hands
 * the question to the gateway over the pharmacy channel and waits. Either way
 * the categories are asked for in parallel and land as they come: the shell
 * serializes them on its side — the amelipro session holds a single current
 * patient — so the screen fills up in the order the portal answers.
 *
 * What comes back is transmitted, never judged. Whether a quantity is too much
 * for a given patient is the pharmacist's call, and this module has no opinion
 * beyond « ADAC listed something » and « ADAC listed nothing ».
 */
const useAdac = () => {
    const amelipro = useAmelipro();
    const locator = useCpsLocator();
    const cpsStore = useCpsStore();

    /** Where the check as a whole stands. */
    const state = ref<'idle' | 'running' | 'done' | 'failed'>('idle');

    /** One entry per category asked for, in the catalogue's order. */
    const checks = ref<AdacCategoryCheck[]>([]);

    /** The gateway that did the work, when it was not this post. */
    const via = ref<string | null>(null);

    /** Why the check as a whole came to nothing, when it did. */
    const refusal = ref<AdacRefusal | null>(null);

    /** When the answer landed, which the compact card says. */
    const checkedAt = ref<Date | null>(null);

    /**
     * Which run the answers landing belong to.
     *
     * A patient stays at the counter for seconds and the next one is detected
     * while a query is still in flight: without this, the previous patient's
     * verdict would be written onto the new one's screen, which is the one
     * mistake this module must never make.
     */
    const generation = ref(0);

    /** The answer as a whole, once the check is done. */
    const gist = computed(() => adacGist(checks.value));

    /**
     * Ask ADAC for one category, wherever the card is.
     *
     * @param nir  The patient NIR.
     * @param slug The category slug.
     */
    const query = async (
        nir: string,
        slug: string,
        local: boolean
    ): Promise<{verdict: AdacVerdict} | {refusal: AdacRefusal}> => {
        if (local || cpsStore.isLocalGateway) {
            const result = await amelipro.queryAdac(nir, slug);

            return 'verdict' in result
                ? {verdict: result.verdict}
                : {refusal: refusalOf(result.error, nir)};
        }

        const answer = await locator.askGateway({kind: 'query', nir, slug});

        if (answer === null) {
            return {refusal: 'unreachable'};
        }

        if (answer.verdict) {
            return {verdict: answer.verdict};
        }

        return {refusal: answer.error ? refusalOf(answer.error, nir) : 'failed'};
    };

    /**
     * Put the panel back to rest, so nothing of one patient is still on screen
     * when the next is detected.
     */
    const reset = (): void => {
        generation.value += 1;
        state.value = 'idle';
        checks.value = [];
        via.value = null;
        refusal.value = null;
        checkedAt.value = null;
    };

    /**
     * Run the check for one patient.
     *
     * @param nir        The patient NIR, as the detection gave it.
     * @param categories The categories to ask about; the whole catalogue by default.
     * @param options    `local` asks this post's own shell whatever the pharmacy
     *                   map says about gateways — the configurator's test bench,
     *                   on a post whose sign-in is precisely what is being fixed.
     *                   `emulated` says the patient came from a customer file,
     *                   so a missing NIR is named for what it is.
     */
    const run = async (
        rawNir: unknown,
        categories: readonly AdacCategory[] = ADAC_CATEGORIES,
        options: {local?: boolean; emulated?: boolean} = {}
    ): Promise<void> => {
        if (state.value === 'running') {
            return;
        }

        reset();

        const round = generation.value;
        const nir = portalNir(rawNir);

        // Without a NIR there is no patient to ask about: the portal selects
        // its current patient by NIR and by nothing else. An OCR detection
        // that read a name but no card gets this, and it is the screen's job
        // to say the card is what is missing. A value that is not a NIR — an
        // emulated detection carries the API's insured identifier, a partial
        // number — is refused here rather than sent to a portal that answers
        // it with a generic 500.
        if (!nir) {
            const given = rawNir === null || rawNir === undefined ? '' : String(rawNir).replace(/\s/g, '');

            state.value = 'failed';
            // An emulation now carries the patient's NIR, as a card would: a
            // number of a NIR's length that fails the check is a NIR the portal
            // would refuse, emulated or not. Only a shorter value — the
            // insured identifier older emulations sent — is the emulation's.
            if (given.length >= 13) {
                refusal.value = 'invalid-nir';
            } else {
                refusal.value = options.emulated ? 'emulated' : given ? 'invalid-nir' : 'no-nir';
            }

            return;
        }

        const local = options.local === true;

        state.value = 'running';
        via.value = local || cpsStore.isLocalGateway ? null : cpsStore.remoteGateway?.hostname ?? null;
        checks.value = categories.map(category => ({category, state: 'running', verdict: null, refusal: null}));

        await Promise.all(categories.map(async (category, index) => {
            const outcome = await query(nir, category.slug, local);

            // The patient changed while this was in flight: the answer is
            // about somebody who has left the counter, and is dropped.
            if (round !== generation.value) {
                return;
            }

            checks.value[index] = 'verdict' in outcome
                ? {category, state: 'done', verdict: outcome.verdict, refusal: null}
                : {category, state: 'failed', verdict: null, refusal: outcome.refusal};
        }));

        if (round !== generation.value) {
            return;
        }

        const failures = checks.value.filter(check => check.state === 'failed');

        // One category refusing is worth showing next to the others; every
        // category refusing is not about the categories at all — the session,
        // the card or the gateway is what failed, and that is what is named.
        state.value = failures.length === checks.value.length ? 'failed' : 'done';
        refusal.value = failures.length === checks.value.length ? failures[0]?.refusal ?? 'failed' : null;
        checkedAt.value = new Date();
    };

    return {
        state,
        checks,
        via,
        refusal,
        gist,
        checkedAt,
        run,
        reset,
    };
};

export default useAdac;
